import { post } from '@rails/request.js';
import { Controller } from 'stimulus';

export default class extends Controller {
  static targets = ['shoppable', 'hideable', 'override', 'title', 'total'];
  static outlets = ['boards--client-visibility-dropdown'];
  static values = { boardHidden: Array };

  #state;

  get boardHidden() {
    return (this.#state ??= new Set(this.boardHiddenValue));
  }

  hideableTargetConnected(el) {
    if (this.boardHidden.has(el.dataset.hideTarget)) {
      el.classList.add('hidden-from-client');
    }
  }

  shoppableTargetConnected(el) {
    if (this.boardHidden.has('visibility')) {
      el.classList.add('shoppable--hidden-from-client');
    }
  }

  setBoardOption(key, hidden) {
    if (hidden) {
      this.boardHidden.add(key);
    } else {
      this.boardHidden.delete(key);
    }

    if (key === 'visibility') {
      this.shoppableTargets.forEach((el) =>
        el.classList.toggle('shoppable--hidden-from-client', hidden)
      );
      this.titleTargets.forEach((el) => el.classList.toggle('hidden-from-client', hidden));
    } else if (key === 'total-price') {
      this.totalTargets.forEach((el) => el.classList.toggle('hidden-from-client', hidden));
    } else {
      this.#hideablesFor(key).forEach((el) => el.classList.toggle('hidden-from-client', hidden));
    }

    this.#applyBoardOverride(key, hidden);
  }

  async toggleProductOption({ target }) {
    const card = target.closest('[data-board-shoppable-id]');
    const key = target.value;
    const hidden = target.checked;

    this.#applyToCard(card, key, hidden);

    const { kind, boardShoppableId } = card.dataset;
    const response = await post(`/shoppables/${kind}/${boardShoppableId}/xhr_hide_from_client`, {
      body: { target: key, set: hidden },
    });
    if (!response.ok) {
      return;
    }

    const data = await response.json;
    this.updateTotal(data?.formatted_board_cost);
    this.dispatch('shoppables-changed', { prefix: 'boards', target: document });
    this.dispatch('visibility-changed', { prefix: 'boards', target: document });
    if (data?.formatted_sidedoor_product_price) {
      const price = card.querySelector('.shoppable__price');
      if (price) {
        price.innerHTML = data.formatted_sidedoor_product_price;
      }
    }
    this.#syncMasterSwitches(data?.board_hidden);
  }

  setPerProductSettingsVisible(visible) {
    this.element.classList.toggle('show-per-product-visibility', visible);
    document.body.classList.toggle('show-per-product-visibility', visible);
  }

  // Also called by the "What Client Sees" dropdown after a board-wide option changes the total.
  updateTotal(formattedCost) {
    if (!formattedCost) {
      return;
    }
    const cost = this.element.querySelector('.board-products__total-board-cost');
    if (cost) {
      cost.innerHTML = formattedCost;
    }
  }

  #applyToCard(card, key, hidden) {
    // The whole product card.
    if (key === 'visibility') {
      card.classList.toggle('shoppable--hidden-from-client', hidden);
      return;
    }

    // Swatch names cannot be hidden (parity with the legacy control).
    if (key === 'name' && card.dataset.kind === 'swatch') {
      return;
    }

    this.hideableTargets
      .filter((el) => el.dataset.hideTarget === key && card.contains(el))
      .forEach((el) => el.classList.toggle('hidden-from-client', hidden));
  }

  #hideablesFor(key) {
    return this.hideableTargets.filter((el) => el.dataset.hideTarget === key);
  }

  // Push the board-wide roll-up (from the server) back to the "What Client Sees" master
  // switches so, e.g., hiding the last visible product re-checks "Hide All Products".
  #syncMasterSwitches(boardHidden) {
    if (!boardHidden) {
      return;
    }

    this.#state = new Set(boardHidden);

    // Keep the board-level "Product List" heading (preview only) in step with the rolled-up
    // visibility flag, mirroring what setBoardOption does on the master-switch path.
    this.titleTargets.forEach((el) =>
      el.classList.toggle('hidden-from-client', this.#state.has('visibility'))
    );

    if (this.hasBoardsClientVisibilityDropdownOutlet) {
      this.boardsClientVisibilityDropdownOutlets.forEach((ctrl) => ctrl.syncOptions(boardHidden));
    }
  }

  #applyBoardOverride(key, hidden) {
    this.overrideTargets
      .filter((input) => input.value === key)
      .forEach((input) => {
        input.checked = hidden;
      });
  }
}
